import { type Op } from 'quill-delta';

import {
  type CollabFilter,
  type CollabFilterJSON,
  type MutableCollabFilter,
} from './view-filter';
import {
  type CollabGroupingJSON,
  type CollabGrouping,
  type MutableCollabGrouping,
} from './view-grouping';
import {
  type CollabViewSortType,
  type MutableSortArray,
  type SortArray,
} from './view-sort';

export enum CollabLayoutType {
  TABLE = 'table',
  GALLERY = 'gallery',
  BOARD = 'board',
  CALENDAR = 'calendar',
  CARD = 'card', // Deprecated: Card referred as Gallery
}

export enum CollabCardWidth {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum CollabCoverFieldType {
  None = 'none',
  Default = 'default',
  ID = 'id',
}

export enum CollabCalculationTypes {
  SUM = 's',
  COUNT = 'c',
  COUNT_EMPTY = 'ce',
  COUNT_TOTAL = 'ct',
  COUNT_UNIQUE = 'cu',
  PERCENT_EMPTY = 'pe',
  PERCENT_NOT_EMPTY = 'pne',
  AVERAGE = 'a',
  MAX = 'ma',
  MIN = 'mi',
}

export type CollabCoverField =
  | {
      type: CollabCoverFieldType.None;
    }
  | {
      type: CollabCoverFieldType.Default;
    }
  | {
      type: CollabCoverFieldType.ID;
      id: string;
    };

export type DatabaseViewJSON = {
  name: string;
  sort: CollabViewSortType | undefined;

  layoutType: CollabLayoutType;
  cardWidth: CollabCardWidth;
  coverField: CollabCoverField;
  showFieldNames: boolean;
  groupFieldId?: string;
  dateFieldId: string | undefined;
  showNumberedEntries: boolean;

  fields: Record<string, boolean> | undefined;

  filter: CollabFilterJSON | undefined;

  calculations: Record<string, CollabCalculationTypes>;

  sorts: CollabViewSortType[];

  grouping: CollabGroupingJSON | undefined;
};

export interface View {
  get name(): string;
  get sort(): CollabViewSortType | undefined;

  get layoutType(): CollabLayoutType;
  get cardWidth(): CollabCardWidth;
  get coverField(): CollabCoverField;
  get showFieldNames(): boolean;
  get groupFieldId(): string | undefined;
  get dateFieldId(): string | undefined;
  get showNumberedEntries(): boolean;

  get fields(): Readonly<Record<string, boolean> | undefined>;

  get filter(): CollabFilter | undefined;

  get sorts(): SortArray;

  get grouping(): CollabGrouping | undefined;

  get calculations(): Readonly<Record<string, CollabCalculationTypes>>;
  setCalculation(fieldKey: string, value: CollabCalculationTypes): void;
  removeCalculation(fieldKey: string): void;

  toJSON(): DatabaseViewJSON;
}

export interface MutableView extends View {
  applyNameDelta(delta: Op[]): void;

  set layoutType(data: CollabLayoutType);
  set cardWidth(data: CollabCardWidth);
  set coverField(data: CollabCoverField);
  set showFieldNames(data: boolean);
  set groupFieldId(data: string | undefined);
  set dateFieldId(data: string | undefined);
  set showNumberedEntries(data: boolean);

  setFieldVisibility(
    fieldKey: string,
    fieldValue: boolean,
    dbFields: readonly string[],
    options?: { includeNewFieldInValidation?: boolean }
  ): void;
  resetFieldVisibility(): void;
  removeFieldVisibilityField(fieldKey: string, dbFields: readonly string[]): void;

  set sort(data: CollabViewSortType | undefined);

  setFilter(val: CollabFilterJSON): MutableCollabFilter;
  clearFilter(): void;

  initGrouping(): MutableCollabGrouping;
  setGrouping(value: CollabGroupingJSON): MutableCollabGrouping;
  clearGrouping(): void;

  get filter(): MutableCollabFilter | undefined;
  get grouping(): MutableCollabGrouping | undefined;
  get sorts(): MutableSortArray;
  initializeSorts(): void;
}
